import type { UploadsController } from '../use-uploads';

import { Helmet } from '@dr.pogodin/react-helmet';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-typed-form';
import { Color, Style } from 'src/constants';

import { Api, useApiFormSubmit } from '../../../api';
import { Button, PageWrap, RouterLink, Stack, Text } from '../../../components';
import { useCompany } from '../../../contexts/ClientDataContext';
import { FieldTextRequired, FormWrap } from '../../../form';
import { createFormValidator } from '../../../form';
import FieldUploads from '../components/FieldUploads';
import FieldWriter from '../components/FieldWriter';

export default function CreateTicketPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const company = useCompany(companySlug!);
  const navigate = useNavigate();
  const uploads = React.useRef<UploadsController | null>(null);

  const formSubmit = useApiFormSubmit('POST', '/tickets', {
    onSuccess: () => {
      navigate(`/company/${companySlug}/tickets`);
    },
  });
  const { formErrorList, getField, handleSubmit, isLoading } = useForm<
    Partial<Api.Ticket.W_Create>
  >({
    defaultValues: { company: `/companies/${company.id}` },
    validator: createFormValidator({
      title: { strictPresence: true },
      uploads: { upload: true },
    }),
    onSubmit: formSubmit,
  });

  return (
    <PageWrap limitWidth="medium" background={Color.neutral5}>
      <Helmet>
        <title>{`New Ticket | ${company.name}`}</title>
      </Helmet>
      <Stack gap={1.5}>
        <Stack gap={0.25}>
          <Stack style={Style.alignStart}>
            <RouterLink to={`/company/${companySlug}/tickets`}>
              <Stack style={[Style.row, Style.alignCenter]} gap={0.25}>
                <ArrowLeftIcon size={16} color={Color.neutral80} />
                <Text typeface="body.medium">Maintenance Tickets</Text>
              </Stack>
            </RouterLink>
          </Stack>
          <Text typeface="headline">Create New Ticket</Text>
        </Stack>
        <FormWrap formErrorList={formErrorList} handleSubmit={handleSubmit}>
          <Stack gap={1.5}>
            <FieldTextRequired
              field={getField('title')}
              placeholder="Ticket Title"
            />
            <FieldWriter
              field={getField('body')}
              onPasteFiles={(files) =>
                uploads.current?.addPastedFiles(files) ?? false
              }
            />
            <FieldUploads field={getField('uploads')} controllerRef={uploads} />

            <Stack style={Style.alignEnd}>
              <Button
                type="submit"
                disabled={isLoading}
                label="Submit Ticket"
              />
            </Stack>
          </Stack>
        </FormWrap>
      </Stack>
    </PageWrap>
  );
}
